import * as React from 'react';
import { X } from 'lucide-react';
import { Alert, type AlertProps } from './Alert';
import { Button } from './Button';

export interface NearLimitBannerProps
  extends Omit<AlertProps, 'tone' | 'actions' | 'children'> {
  /**
   * What is nearly used up, and what the next plan allows: "You've used 41
   * of 50 calls this month. Essential allows 250."
   */
  children: React.ReactNode;
  /**
   * Opens the plans. Leave it out for a member who cannot change the plan:
   * the banner is then not worth showing, and the app shows nothing.
   */
  onUpgrade?: () => void;
  upgradeLabel?: React.ReactNode;
  /** Put the banner away. The app remembers it for this limit this month. */
  onDismiss: () => void;
}

/**
 * The nudge before a plan limit: how much is used, what the next plan
 * allows, Upgrade, and a close button.
 *
 *   <NearLimitBanner onUpgrade={openPlans} onDismiss={dismissForMonth}>
 *     You've used 41 of 50 calls this month. Essential allows 250.
 *   </NearLimitBanner>
 *
 * Shown in both apps once usage passes the plan catalogue's threshold, and
 * only to members with `subscription.view`. Remembering the dismissal per
 * limit per month is the app's; this only draws it.
 */
export const NearLimitBanner = React.forwardRef<
  HTMLDivElement,
  NearLimitBannerProps
>(function NearLimitBanner(
  { children, onUpgrade, upgradeLabel = 'Upgrade', onDismiss, ...props },
  ref
) {
  return (
    <Alert
      ref={ref}
      tone="warning"
      actions={
        <>
          {onUpgrade ? (
            <Button size="sm" onClick={onUpgrade}>
              {upgradeLabel}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Dismiss"
            onClick={onDismiss}
          >
            <X />
          </Button>
        </>
      }
      {...props}
    >
      {children}
    </Alert>
  );
});
